import React, { useEffect, useState } from 'react';
import {
  DEMORALIZING_WARCRY_EFFECT_DURATION_MS,
} from '../config/demoralizingWarcryEffect';

const RIPPLE_COUNT = 5;
const RIPPLE_STAGGER_S = 0.22;
const RIPPLE_DURATION_S = 2;

const VARIANT_STYLES = {
  demoralizing: {
    /** @param {number} r */
    radial: (x, y, r) =>
      `radial-gradient(circle ${r}px at ${x}px ${y}px,
        rgba(120, 200, 255, 0.14) 0%,
        rgba(100, 180, 240, 0.06) 35%,
        transparent 65%)`,
    rippleBorder: '3px solid rgba(140, 215, 255, 0.75)',
    rippleBoxShadow: `
      0 0 28px rgba(100, 190, 255, 0.55),
      0 0 56px rgba(80, 170, 255, 0.25),
      inset 0 0 22px rgba(200, 235, 255, 0.2)`,
  },
  /** Light gray — same motion as demoralizing; strategem 325 (Oblivion Pulse) */
  oblivionPulse: {
    /** @param {number} r */
    radial: (x, y, r) =>
      `radial-gradient(circle ${r}px at ${x}px ${y}px,
        rgba(210, 212, 220, 0.16) 0%,
        rgba(175, 178, 190, 0.07) 35%,
        transparent 65%)`,
    rippleBorder: '3px solid rgba(195, 200, 210, 0.8)',
    rippleBoxShadow: `
      0 0 28px rgba(150, 155, 170, 0.5),
      0 0 56px rgba(120, 125, 140, 0.22),
      inset 0 0 22px rgba(230, 232, 238, 0.18)`,
  },
};

/**
 * Full-viewport ripple waves expanding from the battlefield center.
 * @param {'demoralizing'|'oblivionPulse'} [variant] — 113 blue wash; 325 light gray (same animation).
 */
export const ShockwaveEffect = ({
  x,
  y,
  onComplete,
  animationKey,
  variant = 'demoralizing',
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const styles = VARIANT_STYLES[variant] ?? VARIANT_STYLES.demoralizing;
  const animPrefix =
    variant === 'oblivionPulse' ? 'oblivion-pulse-shockwave' : 'demoralizing-warcry';

  useEffect(() => {
    if (!animationKey) return;

    const done = setTimeout(() => {
      setIsVisible(false);
      if (onComplete) onComplete();
    }, DEMORALIZING_WARCRY_EFFECT_DURATION_MS);

    return () => clearTimeout(done);
  }, [animationKey, onComplete]);

  if (!isVisible) {
    return null;
  }

  const tintRadiusPx =
    typeof window !== 'undefined'
      ? Math.round(Math.max(window.innerWidth, window.innerHeight) * 0.95)
      : 1400;

  return (
    <div
      className={
        variant === 'oblivionPulse'
          ? 'oblivion-pulse-shockwave-fullscreen'
          : 'demoralizing-warcry-fullscreen'
      }
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden',
      }}
      data-animation-key={animationKey}
    >
      {/* Soft radial wash from center */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: styles.radial(x, y, tintRadiusPx),
          animation: `${animPrefix}-tint 1.4s ease-out forwards`,
        }}
      />

      {/* Origin point for concentric waves */}
      <div
        style={{
          position: 'absolute',
          left: `${x}px`,
          top: `${y}px`,
          width: 1,
          height: 1,
        }}
      >
        {Array.from({ length: RIPPLE_COUNT }, (_, i) => (
          <div
            key={i}
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '96px',
              height: '96px',
              marginLeft: '-48px',
              marginTop: '-48px',
              borderRadius: '50%',
              border: styles.rippleBorder,
              boxShadow: styles.rippleBoxShadow,
              animation: `${animPrefix}-ripple ${RIPPLE_DURATION_S}s cubic-bezier(0.22, 0.61, 0.36, 1) forwards`,
              animationDelay: `${i * RIPPLE_STAGGER_S}s`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes ${animPrefix}-tint {
          0% { opacity: 0; }
          25% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes ${animPrefix}-ripple {
          0% {
            transform: scale(0);
            opacity: 0.92;
          }
          100% {
            transform: scale(42);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default ShockwaveEffect;
